import {
	Button,
	Divider,
	IconButton,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { ArrowDownRight, ArrowLeft, ArrowUpRight, Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useLeadMutation } from "web_myplann/Api/Finances/useLeadMutation";
import { MyPlannBarChart } from "web_myplann/Dashboard/Components/BarChart";
import { MyPlannPieChart } from "web_myplann/Dashboard/Components/PieChart";
import { useFinanceData } from "web_myplann/Finances/Helpers/getFinanceData";
import { useOnlineStatus } from "web_myplann/Infrastructure/Hooks/useOnlineStatus";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	getResourcePath,
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { formatCurrency } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { ContactAgentDialog } from "web_myplann/Shared/Components/ContactAgentDialog";
import { CurrencyCode } from "web_myplann/Shared/constants";
import { HeaderIconButton } from "web_myplann/Shared/Header/HeaderIconButton";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { moneyFormatter } from "web_myplann/Shared/Utils/formatters";
import {
	DANGER_MYPLANN_DARK,
	DANGER_MYPLANN_LIGHT,
	FINANCES_EXPENSES_RED,
	FINANCES_INCOME_GREEN,
	getBalanceColor,
	SUCCESS_MYPLANN_DARK,
	SUCCESS_MYPLANN_LIGHT,
} from "web_myplann/Utils/constants";

export const FinancesIntro = () => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();

	const isOnline = useOnlineStatus();

	const [isDialogOpen, setIsDialogOpen] = useState(false);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const { incomeData, totalIncome, totalExpenses, expenseData } =
		useFinanceData();
	const { mutate: makeLead } = useLeadMutation();

	const balance: number = totalIncome - totalExpenses;
	const isFromProfile = location.state?.from === "profile";
	const isFinished = location.state?.isFinished === true;
	const wizardDepth: number = location.state?.wizardDepth ?? 0;

	const handleFinishedBack = () => {
		if (wizardDepth > 0) {
			navigate(-wizardDepth);
		} else {
			navigate(
				isFromProfile
					? Routing.Profile.ProfileDetail.path()
					: Routing.Dashboard.path(),
				{ replace: true },
			);
		}
	};

	const handleNavigate = () => {
		if (!isOnline) {
			toast.error(t(Resources.Finances.Offline.editError));
			return;
		}
		navigate(Routing.Finances.Income.path(), {
			state: {
				from: location.state?.from,
				wizardDepth: (location.state?.wizardDepth ?? 0) + 1,
			},
		});
	};

	const onContactAgent = () => {
		setIsDialogOpen(false);
		makeLead({
			expense: totalExpenses,
			income: totalIncome,
		});
	};

	return (
		<MyPlannLayout
			disableContentPadding
			topBar={
				<NavigationHeader
					title={t(Resources.Finances.title)}
					defaultRoute={isFinished ? undefined : Routing.Dashboard.path()}
					alwaysUseDefaultRoute={!isFromProfile}
					startComponent={
						isFinished ? (
							<HeaderIconButton onClick={handleFinishedBack}>
								<ArrowLeft color={theme.palette.primary.main} />
							</HeaderIconButton>
						) : undefined
					}
					endComponent={
						(totalIncome !== 0 || totalExpenses !== 0) && (
							<IconButton onClick={handleNavigate}>
								<Pencil color={theme.palette.primary.main} />
							</IconButton>
						)
					}
				/>
			}
		>
			{totalIncome === 0 && totalExpenses === 0 ? (
				<Stack
					alignItems="center"
					justifyContent="center"
					sx={{
						height: "100%",
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
					}}
					width="90%"
					gap={6.4}
				>
					<Stack width="100%" alignItems="center">
						<Stack
							width="80%"
							alignItems="center"
							justifyContent="center"
							pb={2.4}
						>
							<MyPlannBarChart data={[totalIncome, totalExpenses]} />
						</Stack>
						<Stack alignItems="center" gap={0.4}>
							<Typography
								variant="deprecatedH3"
								color={theme.palette.deprecated.grey[700]}
							>
								{t(Resources.Finances.Intro.title)}
							</Typography>
							<Typography
								variant="deprecatedCaption"
								color={theme.palette.deprecated.grey[500]}
							>
								{t(Resources.Finances.Intro.description)}
							</Typography>
						</Stack>
					</Stack>
					<Button fullWidth onClick={handleNavigate}>
						{t(Resources.Finances.Intro.button)}
					</Button>
				</Stack>
			) : (
				<Stack p={1.6} pt={4} gap={3.2}>
					<Stack>
						<Typography
							textAlign="center"
							variant="deprecatedH3"
							color={theme.palette.deprecated.grey[700]}
						>
							{t(Resources.Finances.Intro.result)}
						</Typography>
						<Typography
							textAlign="center"
							fontSize={20}
							fontWeight={600}
							lineHeight={2}
							color={getBalanceColor(balance, theme)}
						>
							{balance > 0
								? `+${moneyFormatter(balance, t(Resources.Common.CurrencySign))}`
								: `${moneyFormatter(balance, t(Resources.Common.CurrencySign))}`}
						</Typography>
						<Stack width="100%" alignItems="center" justifyContent="center">
							<MyPlannBarChart
								data={[totalIncome, totalExpenses]}
								height={200}
							/>
						</Stack>
					</Stack>

					{balance && (
						<Stack
							bgcolor={theme.palette.deprecated.grey[100]}
							borderRadius={4}
							p={1.6}
							gap={1.2}
						>
							<Stack gap={0.8}>
								<Typography
									variant="deprecatedH3"
									color={theme.palette.deprecated.grey[700]}
								>
									{balance > 0
										? t(Resources.Finances.Intro.surplusTitle)
										: t(Resources.Finances.Intro.deficitTitle)}
								</Typography>
								<Typography
									variant="deprecatedBody1"
									color={theme.palette.deprecated.grey[500]}
								>
									<Trans
										i18nKey={getResourcePath(
											balance > 0
												? Resources.Finances.Intro.surplusDescription
												: Resources.Finances.Intro.deficitDescription,
										)}
										values={{
											balance: formatCurrency(
												Math.abs(balance),
												CurrencyCode.CZK,
											),
										}}
										components={{
											balance: (
												<Typography
													component="span"
													fontSize={16}
													fontWeight={700}
													color={
														balance > 0
															? theme.palette.mode === "light"
																? SUCCESS_MYPLANN_DARK
																: SUCCESS_MYPLANN_LIGHT
															: theme.palette.mode === "light"
																? DANGER_MYPLANN_DARK
																: DANGER_MYPLANN_LIGHT
													}
												/>
											),
										}}
									/>
								</Typography>
							</Stack>

							<IconButton
								sx={{
									p: 0,
									alignItems: "center",
									display: "flex",
									flexDirection: "row",
									alignSelf: "flex-start",
								}}
								onClick={() => setIsDialogOpen(true)}
								disabled={!isOnline}
							>
								<Typography
									variant="deprecatedBodyStrong"
									color={
										isOnline
											? theme.palette.primary.main
											: theme.palette.action.disabled
									}
								>
									{t(Resources.Finances.Intro.contactAdvisor)}
								</Typography>
							</IconButton>
						</Stack>
					)}

					<Divider sx={{ borderColor: theme.palette.deprecated.grey[300] }} />
					<MyPlannPieChart
						data={incomeData}
						baseColor={FINANCES_INCOME_GREEN}
						centerElement={
							<Stack>
								<Stack
									direction="row"
									alignItems="center"
									justifyContent="center"
									gap={0.4}
								>
									<ArrowUpRight
										size={16}
										color={theme.palette.deprecated.grey[500]}
									/>
									<Typography
										variant="deprecatedH3"
										color={theme.palette.deprecated.grey[700]}
									>
										{t(Resources.BarChart.income)}
									</Typography>
								</Stack>
								<Typography fontSize={20} fontWeight={600}>
									{moneyFormatter(
										totalIncome,
										t(Resources.Common.CurrencySign),
									)}
								</Typography>
							</Stack>
						}
					/>
					<Divider sx={{ borderColor: theme.palette.deprecated.grey[300] }} />
					<MyPlannPieChart
						data={expenseData}
						baseColor={FINANCES_EXPENSES_RED}
						centerElement={
							<Stack>
								<Stack
									direction="row"
									alignItems="center"
									justifyContent="center"
									gap={0.4}
								>
									<ArrowDownRight
										size={16}
										color={theme.palette.deprecated.grey[500]}
									/>
									<Typography
										variant="deprecatedH3"
										color={theme.palette.deprecated.grey[700]}
									>
										{t(Resources.BarChart.expenses)}
									</Typography>
								</Stack>
								<Typography fontSize={20} fontWeight={600}>
									{moneyFormatter(
										totalExpenses,
										t(Resources.Common.CurrencySign),
									)}
								</Typography>
							</Stack>
						}
					/>
				</Stack>
			)}
			<ContactAgentDialog
				open={isDialogOpen}
				onClose={() => setIsDialogOpen(false)}
				onConfirm={onContactAgent}
			/>
		</MyPlannLayout>
	);
};
